import React, { useContext, useEffect, useState } from "react";
import ModalWrapper from "../../components/ui/modals/ModalWrapper";
import Button from "../../components/ui/buttons/Button";
import LanguageToggle from "../../components/ui/forms/LanguageToggle";
import { CoreContext } from "../../context/global/CoreContext";
import { LANGUAGE_CONFIG, UI_COPY, uiText } from "../../config";
import { useUiText } from "../../hooks/useUiText";
import { completeNewUserPrompt } from "../../services/api/core";
import SMSPermissionSection from "./SMSPermissionSection";

/**
 * Mobile-first Home onboarding modal: optional SMS opt-in, then language.
 * Shown when `show_new_user_prompt` is true, or as a fallback when SMS
 * permission is still Not Set (`sms_permission_id` 3). SMS fields appear
 * only in that Not Set case.
 *
 * @param {boolean} isOpen Modal visibility.
 * @param {Function} onComplete Called after the prompt is persisted as done.
 * @param {boolean} showSMS Whether to include the SMS permission section.
 * @param {string} [initialPhoneNumber=""] Pre-filled SMS number.
 * @returns {JSX.Element|null}
 */
const NewUserModal = ({
  isOpen,
  onComplete,
  showSMS,
  initialPhoneNumber = "",
}) => {
  const { updatePreferredLanguage } = useContext(CoreContext);
  const { language, t } = useUiText();
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [smsSaved, setSmsSaved] = useState(false);
  const [languageError, setLanguageError] = useState("");
  const [completeError, setCompleteError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSmsSaved(false);
      setLanguageError("");
      setCompleteError("");
    }
  }, [isOpen]);

  /**
   * Persist the selected language immediately so modal and app copy update.
   *
   * @param {string} nextLanguage Preferred-language code.
   * @returns {Promise<void>}
   */
  const handleLanguageChange = async (nextLanguage) => {
    if (nextLanguage === language || isUpdatingLanguage) {
      return;
    }

    setIsUpdatingLanguage(true);
    setLanguageError("");
    try {
      await updatePreferredLanguage(nextLanguage);
    } catch (error) {
      console.error("Error updating preferred language:", error);
      setLanguageError(t(UI_COPY.nav.languageUpdateFailed));
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  /**
   * Mark the new-user prompt complete, then notify the parent.
   *
   * @returns {Promise<void>}
   */
  const handleComplete = async () => {
    if (isCompleting) {
      return;
    }

    setIsCompleting(true);
    setCompleteError("");
    try {
      await completeNewUserPrompt();
      onComplete();
    } catch (error) {
      console.error("Error completing new user prompt:", error);
      setCompleteError(t(UI_COPY.newUser.completeError));
    } finally {
      setIsCompleting(false);
    }
  };

  /**
   * Keep the modal open after SMS save and reveal Continue.
   *
   * @returns {void}
   */
  const handleSmsSaved = () => {
    setSmsSaved(true);
  };

  const title = showSMS ? t(UI_COPY.sms.title) : t(UI_COPY.newUser.title);
  const showContinue = !showSMS || smsSaved;
  const otherLanguages = LANGUAGE_CONFIG.OPTIONS.filter(
    (option) => option.code !== language
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={() => {}}
      closeButton={false}
      closeOnOutsideClick={false}
      maxWidth={440}
      className="relative mx-3 w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto rounded-lg bg-white px-5 pb-5 pt-5 shadow-lg sm:mx-0 sm:w-full"
    >
      <header className="mb-0 flex items-center justify-center">
        <h2 className="text-center text-2xl font-bold">{title}</h2>
      </header>

      <div className="flex flex-col">
        {showSMS && (
          <SMSPermissionSection
            isOpen={isOpen}
            onSaved={handleSmsSaved}
            initialPhoneNumber={initialPhoneNumber}
          />
        )}

        <div className={`${showSMS ? "mt-2 border-t border-gray-200 pt-2" : ""} text-center`}>
          {showSMS && (
            <h2 className="mb-0 text-2xl font-bold">
              {t(UI_COPY.newUser.title)}
            </h2>
          )}
          <p className="text-sm leading-6 text-gray-700">
            {t(UI_COPY.newUser.languageHelp)}
          </p>
          <hr className="my-1 border-gray-200" />
          {otherLanguages.map((option) => (
            <p
              key={option.code}
              className="mb-4 text-xs leading-5 text-gray-400"
            >
              {uiText(UI_COPY.newUser.languageHelp, option.code)}
            </p>
          ))}
          <LanguageToggle
            language={language}
            options={LANGUAGE_CONFIG.OPTIONS}
            disabled={isUpdatingLanguage}
            onChange={handleLanguageChange}
            variant="full"
            ariaLabel={t(UI_COPY.nav.language)}
          />
          {languageError ? (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {languageError}
            </p>
          ) : null}
        </div>

        {showContinue && (
          <>
            {completeError ? (
              <p className="mt-4 text-center text-sm text-red-600" role="alert">
                {completeError}
              </p>
            ) : null}
            <Button
              onClick={handleComplete}
              text={
                isCompleting
                  ? t(UI_COPY.newUser.processing)
                  : t(UI_COPY.newUser.continue)
              }
              variant={isCompleting ? "disabled" : "primary"}
              disabled={isCompleting}
              className="mt-6 min-h-[52px] w-full text-base"
            />
          </>
        )}
      </div>
    </ModalWrapper>
  );
};

export default NewUserModal;
